import { first } from 'lodash';
import React from 'react';
import halalGraphic from 'src/assets/img/category-pages/halal/halal-graphic.svg';
import { DishFirstFoodItemsSegmentFragment, DishFirstSegmentFilter } from 'src/gqlReactTypings.generated.d';
import { useFilterBarState } from 'src/pages/consumer/components/DishFirstHeader/store';
import { CERTIFIED_DIETARY_HERO_BACKGROUND } from 'src/pages/consumer/dish-first/explore/components/PageHero/VerifiedDietaryPageHero/Content';
import { HalalComplianceBanner } from 'src/pages/consumer/dish-first/explore/components/PageHero/VerifiedDietaryPageHero/HalalCompliance/HalalComplianceBanner';
import { UseExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { generateThumborImgUrl } from 'src/shared/design-system/DynamicImage';
import { minScreen } from 'src/shared/utils/StyledComponentUtils';
import styled from 'styled-components';
import { match, P } from 'ts-pattern';
import { DietaryTags } from 'web-common/src/shared/Enums';
import { Colors, Font, ScreenWidth, Spacing } from 'web-common/src/shared/styles';

export const HeroContainer = styled.div<{
  hasFilterBar: boolean;
  desktopBackgroundUrl: string;
  tabletBackgroundUrl: string;
  mobileBackgroundUrl: string;
}>`
  width: 100%;

  @media (min-width: ${ScreenWidth.EXTRA_LARGE}) {
    width: 1600px;
  }

  margin-right: auto;
  margin-left: auto;

  background-image: url(${({ mobileBackgroundUrl }) =>
    generateThumborImgUrl({
      src: mobileBackgroundUrl,
      width: 500,
      height: 230,
    })});
  // Display image in center
  background-position: center;
  background-size: cover;
  margin-top: ${({ hasFilterBar }) => (hasFilterBar ? '48px' : '0')};
  padding: ${Spacing.QUADRUPLE} 0 ${Spacing.DOUBLE};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  ${minScreen(ScreenWidth.SMALL)} {
    background-image: url(${({ tabletBackgroundUrl }) =>
      generateThumborImgUrl({
        src: tabletBackgroundUrl,
        width: 990,
        height: 230,
      })});
    padding: ${Spacing.QUINTUPLE} 0 ${Spacing.DOUBLE_THREE_QUARTERS};
  }

  ${minScreen(ScreenWidth.MEDIUM)} {
    background-image: url(${({ desktopBackgroundUrl }) =>
      generateThumborImgUrl({
        src: desktopBackgroundUrl,
        width: 1200,
        height: 230,
      })});
    padding: ${Spacing.QUINTUPLE} 0 ${Spacing.DOUBLE_THREE_QUARTERS};
  }

  ${minScreen(ScreenWidth.MEDIUM)} {
    margin-top: 0;
  }
`;

const PatternedStrip = styled.div<{ url: string }>`
  background-image: URL('${({ url }) => url}');
  background-repeat: repeat-x;
  height: 56px;
  width: 100%;
`;

const ContentContainer = styled.div`
  height: 100%;
  width: 100%;
  max-width: 550px;
  padding: 0 ${Spacing.SINGLE} ${Spacing.SINGLE_HALF};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
`;

const HalalGraphicImg = styled.img`
  width: 60px;
  height: 32px;

  ${minScreen(ScreenWidth.SMALL)} {
    width: 90px;
    height: 48px;
  }
`;

const Title = styled.h1`
  width: 100%;
  text-align: center;
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.BOLD};
  font-size: ${Font.Size.XXXL};
  line-height: 120%;
  color: ${Colors.GRAY_DARK_30};

  ${minScreen(ScreenWidth.SMALL)} {
    font-size: ${Font.Size.SEMI_HUGE};
  }
`;

type IProps = {
  className?: string;
  filters: UseExploreUrl<DishFirstSegmentFilter>;
  isOnSeeAllPage: boolean;
  segment?: Pick<DishFirstFoodItemsSegmentFragment, 'displayName'>;
};

export const VerifiedDietaryPageHero: React.FC<IProps> = ({ className, filters, isOnSeeAllPage, segment }) => {
  const filterBarIsShowing = useFilterBarState();
  const dietaryFilter = first(filters.dietaryFilters);

  const title = match({
    dietaryFilter,
    isOnSeeAllPage,
    segment,
  })
    .with(
      { dietaryFilter: DietaryTags.HALAL, isOnSeeAllPage: true, segment: P.not(P.nullish) },
      ({ segment }) => segment.displayName
    )
    .with({ dietaryFilter: DietaryTags.HALAL }, () => 'Taste of Halal')
    .otherwise(() => null);

  const patternStripUrl = match({ dietaryFilter })
    .with(
      { dietaryFilter: DietaryTags.HALAL },
      () =>
        'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/category-pages/halal-dietary/pattern-strips/category-page.svg'
    )
    .otherwise(() => null);

  const heroBackground = dietaryFilter && CERTIFIED_DIETARY_HERO_BACKGROUND[dietaryFilter];
  if (!dietaryFilter || !heroBackground || !title || !patternStripUrl) return null;

  return (
    <>
      <HeroContainer
        className={`pt-16 ${className}`}
        hasFilterBar={filterBarIsShowing}
        desktopBackgroundUrl={heroBackground.backgroundImageDesktop ?? heroBackground.backgroundImage}
        tabletBackgroundUrl={heroBackground.backgroundImageTablet ?? heroBackground.backgroundImage}
        mobileBackgroundUrl={heroBackground.backgroundImage}>
        <PatternedStrip className='absolute top-0 ' url={patternStripUrl} />
        <ContentContainer>
          <HalalGraphicImg src={halalGraphic} />
          <Title className='mb-4'>{title}</Title>
        </ContentContainer>
      </HeroContainer>
      {/* NOTE: Just the one compliance banner for now - but use more snazzy matching as we scale */}
      {dietaryFilter === DietaryTags.HALAL ? <HalalComplianceBanner /> : null}
    </>
  );
};
